import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';

export const SELECTED_BRANCH_KEY = 'librarydesk.selectedBranchId';

const BranchContext = createContext(null);

export const BranchProvider = ({ children }) => {
  const [branches, setBranches] = useState([]);
  const [libraryName, setLibraryName] = useState('LibraryDesk');
  const [selectedBranchId, setSelectedBranchId] = useState(
    () => localStorage.getItem(SELECTED_BRANCH_KEY) || ''
  );
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');

  const refreshBranches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/branches');
      const next = Array.isArray(data?.branches) ? data.branches : [];
      setBranches(next);
      setLibraryName(data?.libraryName || 'LibraryDesk');

      const saved = localStorage.getItem(SELECTED_BRANCH_KEY);
      const validSaved = next.find((branch) => String(branch._id) === String(saved) && branch.status === 'active');
      const fallback = next.find((branch) => branch.isPrimary && branch.status === 'active')
        || next.find((branch) => branch.status === 'active')
        || next[0];
      const nextId = String(validSaved?._id || fallback?._id || '');
      setSelectedBranchId(nextId);
      if (nextId) localStorage.setItem(SELECTED_BRANCH_KEY, nextId);
      else localStorage.removeItem(SELECTED_BRANCH_KEY);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Could not load branches');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => { refreshBranches(); }, [refreshBranches]);

  const selectBranch = useCallback((branchId) => {
    const id = String(branchId || '');
    const branch = branches.find((item) => String(item._id) === id);
    if (!branch || branch.status !== 'active') return false;
    localStorage.setItem(SELECTED_BRANCH_KEY, id);
    setSelectedBranchId(id);
    window.dispatchEvent(new CustomEvent('librarydesk:branch-changed', { detail: { branchId: id } }));
    return true;
  }, [branches]);

  const selectedBranch = useMemo(
    () => branches.find((branch) => String(branch._id) === String(selectedBranchId)) || null,
    [branches, selectedBranchId]
  );

  const value = useMemo(() => ({
    branches,
    libraryName,
    selectedBranchId,
    selectedBranch,
    loading,
    error,
    selectBranch,
    refreshBranches,
  }), [branches, libraryName, selectedBranchId, selectedBranch, loading, error, selectBranch, refreshBranches]);

  if (!initialized) {
    return <div className="flex min-h-screen items-center justify-center bg-gray-50"><div className="h-11 w-11 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600" /></div>;
  }
  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export const useBranch = () => {
  const value = useContext(BranchContext);
  if (!value) throw new Error('useBranch must be used inside BranchProvider');
  return value;
};
